import type { ComponentProps } from 'react';

import { cn } from './utils';

/**
 * Real `<table>` semantics, styled with /design.md tokens.
 *
 * Divs with `role="row"` would look identical and lose every screen-reader
 * affordance a table gives for free — row/column announcement, "column 3 of 7",
 * and `aria-sort` on a header meaning anything. A datagrid is the one place
 * that matters most, because it is nothing but rows and columns.
 *
 * Three rules the styling encodes:
 * - **One scroll container, scrolling both ways.** `TableWrap` is the only
 *   thing that scrolls: `overflow-auto` with a max height, so a long list
 *   scrolls *inside* the page instead of pushing the pagination below the fold,
 *   and a wide one scrolls sideways instead of being clipped. `overscroll-contain`
 *   stops a wheel that reaches the end from chaining to the page.
 * - **The header sticks, and it does so on the cells.** `position: sticky` on
 *   `<thead>` is ignored by some engines; on `<th>` it is not. Each `th`
 *   therefore carries its own sticky and its own opaque background — a
 *   transparent one lets rows scroll through it.
 * - **One fixed row height, and cells never wrap.** A grid whose rows jump
 *   cannot be scanned down a column, which is the only reason to use a grid.
 */
export function TableWrap({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'w-full max-h-[calc(100svh-19rem)] overflow-auto overscroll-contain rounded-card border-[1.5px] border-ink bg-surface shadow-brut',
        className,
      )}
      data-slot="table-wrap"
      {...props}
    />
  );
}

export function Table({ className, ...props }: ComponentProps<'table'>) {
  return (
    <table
      className={cn('w-full border-collapse text-left', className)}
      data-slot="table"
      {...props}
    />
  );
}

export function TableHeader({ className, ...props }: ComponentProps<'thead'>) {
  return <thead className={className} data-slot="table-header" {...props} />;
}

export function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
  return <tbody className={className} data-slot="table-body" {...props} />;
}

export function TableRow({ className, ...props }: ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'h-11 border-b border-hairline transition-colors duration-150 last:border-0 data-interactive:cursor-pointer data-interactive:hover:bg-admin-butter/40',
        className,
      )}
      data-slot="table-row"
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'sticky top-0 z-10 h-10 border-b-[1.5px] border-ink bg-warm-alt px-3 align-middle whitespace-nowrap',
        className,
      )}
      data-slot="table-head"
      scope="col"
      {...props}
    />
  );
}

/**
 * The header's own label. A separate component because the size and tracking
 * must be identical whether or not the column is sortable (a sortable one wraps
 * its label in a button) — the two used to render at different sizes, and the
 * cause was `cn` treating a font-size token as a colour. See `utils.ts`.
 */
export function TableHeadLabel({
  className,
  ...props
}: ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        'text-small-label text-text-secondary uppercase',
        className,
      )}
      data-slot="table-head-label"
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: ComponentProps<'td'>) {
  return (
    <td
      className={cn(
        'max-w-[280px] truncate px-3 align-middle text-ui text-text-primary',
        className,
      )}
      data-slot="table-cell"
      {...props}
    />
  );
}
